import { getCart, magentoAddItemToCart, updateItemInCart } from '$lib/js/magento/cart/cart'
import { storeCartItems, storeCheckoutIframe, storeConfigurableChild, storeMiniCartModal, storeStockModal } from '$lib/js/stores'
import { registerAddToCartEvent } from '$lib/js/ga4/events'
import { get } from 'svelte/store'
import { is_set_object } from '$lib/js/helpers'
import { PUBLIC_PROD } from '$env/static/public'
import type { CartInfo, CartProduct } from '$lib/js/types'
import type { FullProduct } from './elasticsearch/types'
import type { SiteScope } from '$lib/js/helpers/i18n'
import { fetchJSON } from '$lib/js/helpers/http'

/**
 * Add item to cart
 */
export async function addToCart(product: FullProduct, qty: number = 1) {
	/* Check if product is already in cart */
	let inCart = get(storeCartItems).filter((e) => {
		return e.sku === product.sku
	})[0]

	/* If product is not in cart */
	if (!inCart) {
		/* If there is stock */
		if (product.stock.source_quantity >= qty
			|| (product.type_id === 'configurable' && is_set_object(get(storeConfigurableChild)) && get(storeConfigurableChild)?.stock.source_quantity >= qty)
		) {
			product.cart_qty = qty

			// Add item to magento cart
			const result = await magentoAddItemToCart(product, qty)

			// If no errors in adding item to cart
			if (result.item_id) {
				// TODO: to make the typing more straightforward, avoid modifying the function arguments
				product.item_id = result.item_id

				// Add cart_item to cart store
				storeCartItems.update((items) => {
					items.push(product)
					return items
				})

				/* Open minicart */
				storeMiniCartModal.set(true)
			}
		} else {
			storeStockModal.set(product.stock.source_quantity)
		}
	} else {
		if (
			product.stock.source_quantity >= inCart.cart_qty + qty
			|| (product.type_id === 'configurable' && is_set_object(get(storeConfigurableChild)) && get(storeConfigurableChild)?.stock.source_quantity >= inCart.cart_qty + qty)
		) {
			product.cart_qty = inCart.cart_qty + qty
			product.item_id = inCart.item_id

			// Update number of items in magento cart
			await updateItemInCart(product)

			storeCartItems.update((items) => {
				return items.map((item) => {
					// Replace the product with the new data
					if (item.sku === product.sku) {
						return product
					}
					return item
				})
			})

			/* Open minicart */
			storeMiniCartModal.set(true)
		} else {
			storeStockModal.set(product.stock.source_quantity)
		}
	}

	if (PUBLIC_PROD === '1') {
		registerAddToCartEvent(product)
	}
	storeCheckoutIframe.set(true)
}

/**
 * Synchronize Magento cart and store cart items
 */
export async function synchronizeCart(): Promise<CartInfo> {
	return await getCart().then(async (cart_data) => {
		let prod_skus: string[] = []
		let prod_qtys: number[] = []
		let prod_item_ids: number[] = []

		if (is_set_object(cart_data.cart)) {
			let cart_items = cart_data.cart.items
			if (Array.isArray(cart_items)) {
				for (let i = 0; i < cart_items.length; i++) {
					prod_skus.push(cart_items[i].sku)
					prod_qtys.push(cart_items[i].qty)
					prod_item_ids.push(cart_items[i].item_id)
				}
			}
		}
		return { skus: prod_skus, qtys: prod_qtys, ids: prod_item_ids }
	})
}

export async function getCartProducts(info: CartInfo, { scope }: { scope: SiteScope }) {
	const payload = await fetchJSON<{ cart_products: CartProduct[] }>(`/${scope}/endpoints/cart_products`, {
		method: 'POST',
		json: info,
	})

	// FIXME: do something if this is null (HTTP error)
	return payload.cart_products
}
